import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import myAxios from "../AxiosUrl";

// Custom Icons as SVG components
const NotificationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
  </svg>
);

const PersonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
  </svg>
);

// Avatar component for dynamic profile images
const HeaderAvatar = ({ user, size = 44, onClick }) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (user) => {
    if (!user) return "A";
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(
        0
      )}`.toUpperCase();
    } else if (user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    } else if (user.lastName) {
      return user.lastName.charAt(0).toUpperCase();
    } else if (user.username) {
      return user.username.charAt(0).toUpperCase();
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "A";
  };

  const avatarStyle = {
    cursor: "pointer",
    position: "relative",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #ff6b6b, #ee5a24)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    width: `${size}px`,
    height: `${size}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2px",
    boxSizing: "border-box",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  };

  useEffect(() => {
    setImageError(false);
  }, [user?.profileImageUrl]);

  return (
    <div
      style={avatarStyle}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.03)";
        e.currentTarget.style.boxShadow =
          "0 6px 20px rgba(255, 107, 107, 0.35)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
      }}
    >
      {user?.profileImageUrl && !imageError ? (
        <img
          src={user.profileImageUrl}
          alt={user.email || "Profile"}
          style={{
            width: `${size - 4}px`,
            height: `${size - 4}px`,
            borderRadius: "10px",
            objectFit: "cover",
            border: "2px solid white",
            display: "block",
          }}
          onError={() => setImageError(true)}
        />
      ) : (
        <div
          style={{
            width: `${size - 4}px`,
            height: `${size - 4}px`,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            color: "#ffffff",
            fontSize: `${size * 0.35}px`,
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid white",
          }}
        >
          {getInitials(user)}
        </div>
      )}

      {/* Online Status Indicator */}
      <div
        style={{
          position: "absolute",
          top: "-6px",
          right: "-6px",
          width: "14px",
          height: "14px",
          background: "linear-gradient(135deg, #10b981, #059669)",
          borderRadius: "50%",
          border: "3px solid white",
          boxShadow:
            "0 2px 8px rgba(16, 185, 129, 0.5), 0 0 0 1px rgba(16, 185, 129, 0.2)",
        }}
      ></div>
    </div>
  );
};

function Header({ toggleSidebar, isSidebarOpen, isMobile }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await myAxios.get("/profile");

        if (response.data.success) {
          setUser(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
        // Set fallback user data if API fails
        setUser({
          firstName: "Admin",
          lastName: "User",
          email: "admin@example.com",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get display name
  const getDisplayName = () => {
    if (!user) return "Loading...";
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user.firstName) {
      return user.firstName;
    } else if (user.username) {
      return user.username;
    } else if (user.email) {
      return user.email.split("@")[0];
    }
    return "Admin User";
  };

  // Get display email
  const getDisplayEmail = () => {
    return user?.email || "admin@example.com";
  };

  // Log out function
  const handleLogout = async () => {
    try {
      // Call logout endpoint
      await myAxios.post("/logout");

      // Clear local storage
      localStorage.removeItem("token");
      localStorage.removeItem("id");
      localStorage.removeItem("IsAdmin");
      localStorage.removeItem("IsSuper");
      localStorage.removeItem("AdminType");
      localStorage.removeItem("adminType");

      // Navigate to login page
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      // Even if the API call fails, clear local storage and redirect
      localStorage.removeItem("token");
      localStorage.removeItem("id");
      localStorage.removeItem("IsAdmin");
      localStorage.removeItem("IsSuper");
      localStorage.removeItem("AdminType");
      localStorage.removeItem("adminType");
      navigate("/login");
    }
  };

  const handleLinkClick = (href) => {
    setShowDropdown(false);
    navigate(href);
  };

  return (
    <header
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1010,
        height: "63px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 20px",
        margin: 0,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Left section - Logo */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "100%",
        }}
      >
        <div
          style={{
            cursor: "pointer",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              height: "100%",
            }}
          >
            {/* Logo Icon */}
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "linear-gradient(135deg, #ff6b6b, #ee5a24)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(255, 107, 107, 0.3)",
                transform: "rotate(-5deg)",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  color: "white",
                  fontSize: "20px",
                  fontWeight: "800",
                  transform: "rotate(5deg)",
                  lineHeight: 1,
                }}
              >
                T
              </span>
            </div>

            {/* Logo Text */}
            <div
              style={{
                fontSize: "28px",
                fontWeight: "800",
                color: "white",
                letterSpacing: "-0.02em",
                textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              TopSekk
            </div>
          </div>
        </div>
      </div>

      {/* Center section - Search or breadcrumbs could go here */}
      <div style={{ flex: 1 }}></div>

      {/* Right section - Notification and Profile */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? "14px" : "18px",
          height: "100%",
        }}
      >
        {/* Notification Icon */}
        <div
          style={{
            position: "relative",
            cursor: "pointer",
            borderRadius: "12px",
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            color: "white",
            width: "42px",
            height: "42px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div
            style={{
              width: "22px",
              height: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NotificationIcon />
          </div>

          {/* Notification Badge */}
          <div
            style={{
              position: "absolute",
              top: "-4px",
              right: "-4px",
              background: "linear-gradient(135deg, #ff6b6b, #ee5a24)",
              color: "white",
              borderRadius: "50%",
              width: "16px",
              height: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "10px",
              fontWeight: "700",
              border: "2px solid white",
              boxShadow: "0 2px 8px rgba(255, 107, 107, 0.6)",
              animation: "pulse 2s infinite",
              lineHeight: 1,
            }}
          >
            5
          </div>
        </div>

        {/* Profile Menu */}
        <div style={{ position: "relative", flexShrink: 0 }} ref={dropdownRef}>
          {/* Profile Avatar */}
          <HeaderAvatar
            user={user}
            size={44}
            onClick={() => setShowDropdown(!showDropdown)}
          />

          {/* Dropdown Menu */}
          {showDropdown && (
            <div
              style={{
                position: "absolute",
                top: "50px",
                right: "0",
                width: "280px",
                background: "white",
                borderRadius: "20px",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                border: "1px solid rgba(0, 0, 0, 0.05)",
                overflow: "hidden",
                zIndex: 1000,
                animation: "slideDown 0.3s ease",
              }}
            >
              {/* Dropdown Header */}
              <div
                style={{
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  padding: "24px",
                  color: "white",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <HeaderAvatar user={user} size={56} />
                  <div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        marginBottom: "4px",
                      }}
                    >
                      {getDisplayName()}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        opacity: "0.8",
                      }}
                    >
                      {getDisplayEmail()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div style={{ padding: "16px" }}>
                {/* Profile Link */}
                <div
                  onClick={() => handleLinkClick("/edit-profile")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "16px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    marginBottom: "8px",
                    color: "#374151",
                    fontSize: "16px",
                    fontWeight: "500",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#f3f4f6";
                    e.target.style.transform = "translateX(4px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  <div
                    style={{
                      padding: "8px",
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #667eea, #764ba2)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PersonIcon />
                  </div>
                  Profile
                </div>

                {/* Settings Link */}
                <div
                  onClick={() => handleLinkClick("/settings")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "16px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    marginBottom: "16px",
                    color: "#374151",
                    fontSize: "16px",
                    fontWeight: "500",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#f3f4f6";
                    e.target.style.transform = "translateX(4px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  <div
                    style={{
                      padding: "8px",
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #10b981, #059669)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SettingsIcon />
                  </div>
                  Settings
                </div>

                {/* Divider */}
                <div
                  style={{
                    height: "1px",
                    background:
                      "linear-gradient(90deg, transparent, #e5e7eb, transparent)",
                    margin: "16px 0",
                  }}
                ></div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "16px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    border: "none",
                    background: "transparent",
                    width: "100%",
                    color: "#dc2626",
                    fontSize: "16px",
                    fontWeight: "500",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#fef2f2";
                    e.target.style.transform = "translateX(4px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.transform = "translateX(0)";
                  }}
                >
                  <div
                    style={{
                      padding: "8px",
                      borderRadius: "8px",
                      background: "linear-gradient(135deg, #ef4444, #dc2626)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <LogoutIcon />
                  </div>
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.08);
              opacity: 0.85;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
          
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </header>
  );
}

export default Header;
